import React, { useEffect, useState } from 'react';
import {
  Chip,
  Grid,
  Paper,
  Typography,
  Tooltip,
  Collapse,
  useTheme,
  IconButton,
  Theme,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { ExpandMoreOutlined, InfoOutlined } from '@mui/icons-material';
import { RootState } from '@Common/models/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { openToastr } from '@Common/handlers/services';
import Track from 'core/logger/track';

import { setKeywords, userClearedKeywords, userClickedKeyword } from '../actions/systemActions';
import { getNumberOfSentences } from '../handlers/logic';

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    marginRight: '10px',
    marginBottom: '5px',
    border: `1px solid ${theme.palette.text.primary}`,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    '&:focus': {
      backgroundColor: theme.palette.background.paper,
    },
    '&:hover': {
      border: `1px solid ${theme.specialColors.SMZR_KEYWORD}`,
      color: theme.specialColors.SMZR_KEYWORD,
      backgroundColor: '#FEF6EE',
    },
  },
  infoIcon: {
    marginLeft: '5px',
  },
  tooltip: {
    display: 'block',
    margin: '0px',
    padding: '10px',
    width: 230,
    fontSize: '13px',
    borderRadius: '6px',
  },
  // deleteIcon: {
  //   color: theme.specialColors.SMZR_KEYWORD,
  //   margin: '0px 2px 0px 5px',
  //   '&:hover': {
  //     color: theme.specialColors.SMZR_KEYWORD,
  //   },
  // },
  deleteChip: {
    flexDirection: 'row-reverse',
  },
  label: {
    paddingLeft: '0px',
    fontSize: '15px',
    fontWeight: 'bold',
  },
  paperDrawer: {
    width: '100%',
    maxHeight: '147px',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: '0px -2px 6px rgba(0, 0, 0, 0.11)',
  },
  loadingText: {
    paddingLeft: '20px',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    '&::after': {
      content: '" ..."',
      animation: '$dots 1.2s steps(5, end) infinite',
      fontSize: '15px',
    },
  },
  '@keyframes dots': {
    '0%': {
      textShadow: '.25em 0 0 rgba(0,0,0,0),.5em 0 0 rgba(0,0,0,0)',
    },
    '40%': {
      color: theme.palette.grey[50],
      'text-shadow': '.25em 0 0 rgba(0,0,0,0),.5em 0 0 rgba(0,0,0,0)',
    },
    '60%': {
      'text-shadow': `.25em 0 0 ${theme.palette.grey[50]},.5em 0 0 rgba(0,0,0,0)`,
    },
    '100%': {
      textShadow: `.25em 0 0 ${theme.palette.grey[50]}, .5em 0 0 ${theme.palette.grey[50]}`,
    },
  },
  clearAll: {
    color: theme.specialColors.LCS_COLOR,
    cursor: 'pointer',
  },
  dropdownOpen: {
    transform: 'rotate(-180deg)',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  dropdownClosed: {
    transform: 'rotate(0)',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
}));

const TopicsDrawer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme<Theme>();
  const topics = useSelector((state: RootState) => state.smzr.controls.keywords);
  const selectedTopics = useSelector((state: RootState) => state.smzr.controls.selectedKeywords);
  const inputText = useSelector((state: RootState) => state.smzr.input.inputPara);
  const mode = useSelector((state: RootState) => state.common.platform || {});
  const summaryMode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const loadingKeywords = useSelector((state: RootState) => state.smzr.controls.loadingKeywords);
  const [toggleCollapse, setToggleCollapse] = useState(mode.isMobile);
  useEffect(() => {
    if (!inputText.length) dispatch(setKeywords([]));
  }, [inputText]);

  const handleTopicClick = (topic: string) => {
    if (selectedTopics.length > 4 && !selectedTopics.includes(topic)) {
      openToastr('Max of 5 keywords can be selected');
      return;
    }
    dispatch(userClickedKeyword(topic));
  };

  const chipStyle = (topic: string) => {
    if (selectedTopics.includes(topic)) {
      return {
        border: `1px solid ${theme.specialColors.SMZR_KEYWORD}`,
        color: theme.specialColors.SMZR_KEYWORD,
        backgroundColor: '#FEF6EE',
      };
    }
    return {};
  };

  const clearSelection = () => {
    dispatch(userClearedKeywords());
  };

  const toggleDrawer = () => {
    setToggleCollapse(!toggleCollapse);
    Track.getInstance().storeActionEvent(
      `smzr/user/${!toggleCollapse ? 'opened' : 'closed'}TopicsDrawer`
    );
  };

  if (
    (!topics.length && !loadingKeywords) ||
    getNumberOfSentences(inputText) <= 1 ||
    summaryMode !== 'abs'
  ) {
    return null;
  }

  return (
    <>
      <Paper className={classes.paperDrawer}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid
            item
            container
            style={{ margin: '5px 20px 10px' }}
            onClick={() => setToggleCollapse(!toggleCollapse)}
          >
            <Grid item style={{ flexGrow: 1, display: 'flex' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                Select Keywords
              </Typography>
              <Tooltip
                title="Select a single or multiple main keywords to enhance your summary."
                arrow
                placement="top"
                PopperProps={{
                  popperOptions: {
                    // modifiers: {
                    //   offset: {
                    //     enabled: true,
                    //     offset: '0px, 10px',
                    //   },
                    // },
                  },
                }}
                classes={{ tooltip: classes.tooltip }}
              >
                <InfoOutlined
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                  style={{ marginLeft: '2px', cursor: 'pointer', fontSize: '22px' }}
                />
              </Tooltip>
            </Grid>

            {/* <Grid item alignItems="center" style={{ margin: '5px 10px', display: 'flex' }}>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            Highlight
          </Typography>
          <Grid style={{ marginLeft: '5px' }}>
            <AntSwitch />
          </Grid>
        </Grid> */}
            {selectedTopics.length > 1 && (
              <Typography variant="subtitle2" className={classes.clearAll} onClick={clearSelection}>
                Clear All
              </Typography>
            )}
            <IconButton
              className={toggleCollapse ? classes.dropdownClosed : classes.dropdownOpen}
              style={{ padding: '0px', marginLeft: '4px' }}
              onClick={toggleDrawer}
              size="large"
            >
              <ExpandMoreOutlined />
            </IconButton>
          </Grid>
          {loadingKeywords ? (
            <Typography className={classes.loadingText}>Loading</Typography>
          ) : (
            <Collapse
              in={toggleCollapse}
              timeout={800}
              collapsedSize={mode.isMobile ? '0px' : '37px'}
            >
              <Grid item container style={{ padding: '0px 20px 5px' }}>
                {topics.map((topic: string) => (
                  <Chip
                    key={topic}
                    variant="outlined"
                    label={topic}
                    className={classes.chip}
                    onClick={() => handleTopicClick(topic)}
                    style={chipStyle(topic)}
                  />
                ))}
              </Grid>
            </Collapse>
          )}
        </Grid>
      </Paper>
    </>
  );
};

export default TopicsDrawer;
